// Priority order for filling outfield slots as a team grows from 1 player up to
// a full 11-a-side (GK + 10 outfield). Small games just take the first few
// entries; a full squad works out to a recognisable 4-4-2 shape.
/**
 * The outfield shape for each squad size, worked out per size rather than by
 * appending to one priority list.
 *
 * A single append-only list cannot express this. Two defenders should be a pair
 * of centre backs guarding the goal; three should be a left back, a centre back
 * and a right back spread across the width. Going from two to three has to move
 * the first two apart, and an append list can only ever add a third somewhere —
 * which is why small-sided games kept producing two centre backs and no width.
 *
 * Read as (defence – midfield – attack). Anything above eleven a side just adds
 * midfielders.
 */
export const FORMATIONS = {
  1: ['CM'],
  2: ['CB', 'ST'],
  3: ['CB', 'CM', 'ST'],
  4: ['CB', 'CB', 'CM', 'ST'],
  // 2-2-1, not 3-1-1: with only five outfield players, three at the back is
  // more defenders than the rest of the team put together. A back three earns
  // its place from six outfield up, which is where the width is worth having.
  5: ['CB', 'CB', 'CM', 'CM', 'ST'],
  6: ['LB', 'CB', 'RB', 'CM', 'CM', 'ST'],
  7: ['LB', 'CB', 'RB', 'LM', 'CM', 'RM', 'ST'],
  8: ['LB', 'CB', 'CB', 'RB', 'LM', 'CM', 'RM', 'ST'],
  9: ['LB', 'CB', 'CB', 'RB', 'LM', 'CM', 'RM', 'ST', 'ST'],
  10: ['LB', 'CB', 'CB', 'RB', 'LM', 'CM', 'CM', 'RM', 'ST', 'ST'],
};

// Broad zones used for fallback matching when nobody available lists the exact
// position needed.
export const ZONES = {
  GK: 'gk',
  CB: 'def',
  LB: 'def',
  RB: 'def',
  CDM: 'dmid',
  CM: 'mid',
  LM: 'mid',
  RM: 'mid',
  LW: 'att',
  RW: 'att',
  ST: 'att',
};

// Approximate pitch coordinates (percent of width/height) for each position,
// attacking toward the top of the pitch.
export const POSITION_COORDS = {
  GK: { x: 50, y: 91 },
  CB: { x: 50, y: 75 },
  LB: { x: 16, y: 71 },
  RB: { x: 84, y: 71 },
  CDM: { x: 50, y: 58 },
  CM: { x: 50, y: 44 },
  LM: { x: 17, y: 46 },
  RM: { x: 83, y: 46 },
  LW: { x: 19, y: 23 },
  RW: { x: 81, y: 23 },
  ST: { x: 50, y: 13 },
};

/**
 * The positions one team needs to fill.
 *
 * `includeKeeper` is false when nobody in the squad actually keeps goal. Most
 * casual games are played that way — everyone takes a turn in net during the
 * match — and stamping GK on whoever the algorithm had left over invents a
 * commitment nobody made. Better to put everyone outfield and let the rotation
 * happen on the pitch, where it already does.
 */
export function buildFormationSlots(teamSize, { includeKeeper = true } = {}) {
  const slots = [];
  if (includeKeeper && teamSize > 0) slots.push('GK');

  const outfield = Math.max(0, teamSize - slots.length);
  if (outfield === 0) return slots;

  const shape = FORMATIONS[outfield];
  if (shape) return [...slots, ...shape];

  // Bigger than eleven a side: take the fullest shape we have and pad the
  // middle, which is where spare players actually end up in a park game.
  const biggest = FORMATIONS[10];
  return [...slots, ...biggest, ...Array(outfield - biggest.length).fill('CM')];
}

export const PITCH_LEFT = 13;
export const PITCH_RIGHT = 87;
export const MIN_GAP = 19; // closest two tokens may sit before they visibly overlap

/**
 * Spreads a row of players apart until nothing overlaps, keeping each group
 * centred on where its members actually belong.
 *
 * The obvious approach — walk left to right and shove anything too close a bit
 * further right — is wrong, and visibly so. Two centre backs both want x=50, so
 * the second gets pushed to 69 and the pair ends up centred on 58.5: the whole
 * defence drifts right, and with a right back already at 84 the row gets
 * squashed against the touchline too.
 *
 * Instead, players that would collide are gathered into a cluster and the
 * cluster is centred on the average of where its members wanted to be. Two CBs
 * end up at 40.5 and 59.5 — evenly either side of the centre spot, which is
 * where two centre backs stand.
 */
export function spaceOut(xs) {
  const n = xs.length;
  if (n < 2) return [...xs];

  // Shrink the gap if the row is too crowded to hold the full one.
  const gap = Math.min(MIN_GAP, (PITCH_RIGHT - PITCH_LEFT) / (n - 1));

  // A cluster is a run of players that must sit exactly `gap` apart. Its ideal
  // centre is the mean of its members' wanted positions.
  const clusters = [];
  const startOf = (c) => c.sum / c.count - ((c.count - 1) * gap) / 2;

  for (let i = 0; i < n; i++) {
    clusters.push({ first: i, count: 1, sum: xs[i] });

    // Merge backwards while this cluster would overlap the one before it.
    while (clusters.length > 1) {
      const b = clusters[clusters.length - 1];
      const a = clusters[clusters.length - 2];
      const aEnd = startOf(a) + (a.count - 1) * gap;
      if (startOf(b) < aEnd + gap - 1e-9) {
        clusters.splice(-2, 2, { first: a.first, count: a.count + b.count, sum: a.sum + b.sum });
      } else break;
    }
  }

  const out = new Array(n);
  clusters.forEach((c) => {
    const start = startOf(c);
    for (let k = 0; k < c.count; k++) out[c.first + k] = start + k * gap;
  });

  // If the row now hangs off an edge, slide the whole thing rather than
  // squashing one end — squashing would reintroduce the drift this fixes.
  const min = Math.min(...out);
  const max = Math.max(...out);
  let shift = 0;
  if (min < PITCH_LEFT) shift = PITCH_LEFT - min;
  if (max + shift > PITCH_RIGHT) shift = PITCH_RIGHT - max;

  return out.map((x) => Math.max(PITCH_LEFT, Math.min(PITCH_RIGHT, x + shift)));
}
